import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/config';
import { authApi } from '../utils/authApi';
import { clearAnalysisDraftForUser } from '../utils/analysisDraftCache';
import {
  clearAuthSession,
  loadAuthSession,
  saveAuthSession,
  type StoredUser,
} from '../utils/authStorage';

interface User {
  id: string;
  email: string;
  name?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (
    email: string,
    password: string,
    rememberMe?: boolean
  ) => Promise<{ success: boolean; error?: string }>;
  register: (firstName: string, lastName: string, email: string, password: string, confirmPassword: string) => Promise<{ success: boolean; error?: string; message?: string }>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  getAuthHeaders: () => Record<string, string>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Global axios defaults (used by many components). Auth uses `authApi` so login always targets API_URL.
axios.defaults.baseURL = API_URL;
axios.defaults.withCredentials = true;
axios.defaults.headers.common['Content-Type'] = 'application/json';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const tokenRef = useRef<string | null>(null);
  /** Tracks current user id for clearing per-user analysis draft when session ends (checkAuth may run with a stale `user` closure). */
  const userIdForDraftRef = useRef<string | null>(null);

  const setAuthToken = (token: string | null) => {
    tokenRef.current = token;
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      authApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
      delete authApi.defaults.headers.common['Authorization'];
    }
  };

  const getAuthHeaders = (): Record<string, string> => {
    const token = tokenRef.current;
    if (token) {
      return { Authorization: `Bearer ${token}` };
    }
    return {};
  };

  const clearLocalAuth = (uid?: string | null) => {
    const id = uid ?? userIdForDraftRef.current;
    if (id) clearAnalysisDraftForUser(id);
    setUser(null);
    setAuthToken(null);
    clearAuthSession();
  };

  const applyAuthSuccess = (
    accessToken: string,
    refreshToken: string | null | undefined,
    nextUser: User | null | undefined,
    rememberMe: boolean
  ) => {
    setAuthToken(accessToken);
    if (nextUser) {
      setUser(nextUser);
      userIdForDraftRef.current = nextUser.id;
    }
    saveAuthSession({
      accessToken,
      refreshToken: refreshToken ?? null,
      user: (nextUser as StoredUser | null | undefined) ?? null,
      rememberMe,
    });
  };

  const tryRefreshSession = async (): Promise<boolean> => {
    const stored = loadAuthSession();
    if (!stored?.refreshToken) return false;
    try {
      const response = await authApi.post('/auth/refresh', {
        refresh_token: stored.refreshToken,
      });
      if (response.data.success && response.data.access_token) {
        applyAuthSuccess(
          response.data.access_token,
          response.data.refresh_token ?? stored.refreshToken,
          response.data.user ?? stored.user,
          stored.rememberMe
        );
        return true;
      }
    } catch (_) {}
    return false;
  };

  const checkAuth = async () => {
    const finish = () => setLoading(false);

    const handleCheckResponse = (response: { data: { authenticated?: boolean; user?: User; access_token?: string; refresh_token?: string } }) => {
      if (response.data.authenticated) {
        const stored = loadAuthSession();
        setUser(response.data.user ?? null);
        if (response.data.user?.id) {
          userIdForDraftRef.current = response.data.user.id;
        }
        if (response.data.access_token) {
          applyAuthSuccess(
            response.data.access_token,
            response.data.refresh_token,
            response.data.user,
            stored?.rememberMe ?? true
          );
        }
        return true;
      }
      clearLocalAuth();
      return false;
    };

    try {
      const response = await authApi.get('/auth/check-auth');
      handleCheckResponse(response);
    } catch (error: unknown) {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      if (status === 401) {
        const refreshed = await tryRefreshSession();
        if (refreshed) {
          try {
            const retry = await authApi.get('/auth/check-auth');
            if (handleCheckResponse(retry)) {
              finish();
              return;
            }
          } catch (_) {}
        }
      }
      clearLocalAuth();
    } finally {
      finish();
    }
  };

  const login = async (email: string, password: string, rememberMe = true) => {
    try {
      const response = await authApi.post('/auth/login', {
        email,
        password,
      });

      if (response.data.success) {
        if (response.data.access_token) {
          applyAuthSuccess(
            response.data.access_token,
            response.data.refresh_token,
            response.data.user,
            rememberMe
          );
        } else if (response.data.user) {
          setUser(response.data.user);
        } else {
          await checkAuth();
        }
        return { success: true };
      }
      return { success: false, error: response.data.error };
    } catch (error: unknown) {
      const axiosErr = axios.isAxiosError(error) ? error : null;
      const msg =
        axiosErr?.response?.data?.error ||
        (axiosErr?.code === 'ERR_NETWORK' || axiosErr?.message === 'Network Error'
          ? `Cannot reach the API at ${API_URL}. Start the backend (port 8000) and confirm this URL in your browser devtools Network tab.`
          : null) ||
        axiosErr?.message ||
        'An error occurred during login';
      return { success: false, error: typeof msg === 'string' ? msg : 'An error occurred during login' };
    }
  };

  const register = async (firstName: string, lastName: string, email: string, password: string, confirmPassword: string) => {
    try {
      const response = await authApi.post('/auth/register', {
        first_name: firstName,
        last_name: lastName,
        email,
        password,
        confirm_password: confirmPassword
      });

      if (response.data.success) {
        return { success: true, message: response.data.message };
      } else {
        return { success: false, error: response.data.error };
      }
    } catch (error: unknown) {
      const axiosErr = axios.isAxiosError(error) ? error : null;
      const msg =
        axiosErr?.response?.data?.error ||
        (axiosErr?.code === 'ERR_NETWORK' || axiosErr?.message === 'Network Error'
          ? `Cannot reach the API at ${API_URL}. Is the backend running?`
          : null) ||
        axiosErr?.message ||
        'An error occurred during registration';
      return { success: false, error: typeof msg === 'string' ? msg : 'An error occurred during registration' };
    }
  };

  const logout = async () => {
    const uid = user?.id;
    clearLocalAuth(uid);
    try {
      await authApi.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  useEffect(() => {
    userIdForDraftRef.current = user?.id ?? null;
  }, [user?.id]);

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (!originalRequest || originalRequest._retry) {
          return Promise.reject(error);
        }
        if (error.response?.status !== 401) {
          return Promise.reject(error);
        }
        const url = String(originalRequest.url || '');
        if (
          url.includes('/auth/login') ||
          url.includes('/auth/register') ||
          url.includes('/auth/refresh') ||
          url.includes('/auth/logout')
        ) {
          return Promise.reject(error);
        }
        originalRequest._retry = true;
        const refreshed = await tryRefreshSession();
        if (refreshed && tokenRef.current) {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${tokenRef.current}`;
          return axios.request(originalRequest);
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      const stored = loadAuthSession();
      if (stored?.accessToken) {
        setAuthToken(stored.accessToken);
        if (stored.user) {
          setUser(stored.user);
          userIdForDraftRef.current = stored.user.id;
        }
      }
      await checkAuth();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    loading,
    login,
    register,
    logout,
    checkAuth,
    getAuthHeaders,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
